import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MessageSquarePlus, Search, Send, Square, Archive, Paperclip } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { colors, spacing, typography, borderRadius, breakpoints } from '../styles/theme';
import {
  archiveConversation,
  getTranscript,
  listApprovals,
  listConversations,
  listModels,
  respondToApproval,
  searchConversations,
  sendMessage,
  type AgentEvent,
  type AgentModel,
  type ApprovalSummary,
  type ConversationSummary,
  type TranscriptMessage,
} from '../services/adminAgentApi';
import { ActivityIndicator, ApprovalCard, ToolCard, UsageCard, type ToolActivity } from '../components/admin/agent/AgentCards';
import { UploadPanel } from '../components/admin/agent/UploadPanel';

const Shell = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${spacing[4]};
  height: calc(100vh - 160px);
  min-height: 480px;

  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  overflow-y: auto;

  @media (max-width: ${breakpoints.lg}) {
    max-height: 260px;
  }
`;

const ConversationButton = styled.button<{ $active?: boolean }>`
  text-align: left;
  border: 1px solid ${props => (props.$active ? colors.primary[400] : 'transparent')};
  background: ${props => (props.$active ? colors.primary[50] : 'transparent')};
  border-radius: ${borderRadius.base};
  padding: ${spacing[2]};
  cursor: pointer;
  font-size: ${typography.fontSizes.sm};
  color: ${colors.text.primary};

  &:hover { background: ${colors.background.tertiary}; }
`;

const Main = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.lg};
  overflow: hidden;
`;

const Transcript = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${spacing[3]};

  @media (max-width: ${breakpoints.lg}) {
    max-height: 60vh;
  }
`;

const Bubble = styled.div<{ $role: string }>`
  align-self: ${props => (props.$role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: min(680px, 90%);
  background: ${props => (props.$role === 'user' ? colors.primary[600] : colors.background.secondary)};
  color: ${props => (props.$role === 'user' ? colors.white : colors.text.primary)};
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]};
  font-size: ${typography.fontSizes.sm};
  line-height: ${typography.lineHeights.relaxed};
  /* User text is plain, so it keeps its newlines. Assistant text is Markdown,
     which produces its own block elements. */
  white-space: ${props => (props.$role === 'user' ? 'pre-wrap' : 'normal')};
  word-break: break-word;
`;

const Composer = styled.form`
  display: flex;
  gap: ${spacing[2]};
  padding: ${spacing[3]};
  border-top: 1px solid ${colors.border.light};
`;

const MODEL_KEY = 'eventfinder.admin.agent.model';

const ReasoningNote = styled.div`
  align-self: flex-start;
  max-width: 80%;
  padding: ${spacing[2]} ${spacing[3]};
  border-left: 2px solid ${colors.border.normal};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  font-style: italic;
  white-space: pre-wrap;
`;

const ModelSelect = styled.select`
  align-self: flex-end;
  max-width: 190px;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  background: ${colors.background.primary};
  color: ${colors.text.secondary};
  font-family: inherit;
  font-size: ${typography.fontSizes.sm};
  cursor: pointer;
`;

const TextArea = styled.textarea`
  flex: 1;
  resize: none;
  min-height: 44px;
  max-height: 160px;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  font-family: inherit;
  font-size: ${typography.fontSizes.sm};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1]};
  border: 1px solid ${colors.primary[600]};
  background: ${colors.primary[600]};
  color: ${colors.white};
  border-radius: ${borderRadius.base};
  padding: 0 ${spacing[3]};
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const SearchRow = styled.div`
  display: flex;
  gap: ${spacing[1]};
  align-items: center;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  padding: ${spacing[2]};
  border: 1px solid ${colors.border.normal};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSizes.sm};
`;

const Notice = styled.p`
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.sm};
  margin: 0;
`;

/**
 * One turn can produce several assistant messages interleaved with tool calls,
 * so the live view is an ordered timeline rather than a text blob plus a
 * separate tool list. Without the ordering the tool cards all bunch up away
 * from the text they belong to.
 */
type StreamPart =
  | { kind: 'text'; text: string; closed: boolean }
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool'; tool: ToolActivity };

type Phase = 'working' | 'thinking' | 'writing' | 'approval';

interface StreamState {
  parts: StreamPart[];
  usage: Record<string, unknown> | null;
  traceId: string | null;
  phase: Phase | null;
}

const PHASE_LABELS: Record<Phase, string> = {
  working: 'Working',
  thinking: 'Thinking',
  writing: 'Writing a reply',
  approval: 'Waiting for your approval',
};

/**
 * The label is derived rather than stored because the approval events fire
 * before the tool actually runs; storing it would leave "Working" on screen for
 * the whole call instead of naming the tool the user is waiting on.
 */
function statusLabel(stream: StreamState): string | null {
  if (!stream.phase) return null;
  if (stream.phase === 'approval') return PHASE_LABELS.approval;

  const running = stream.parts.find(
    (part): part is Extract<StreamPart, { kind: 'tool' }> =>
      part.kind === 'tool' && part.tool.status === 'running');

  return running ? `Running ${running.tool.toolName ?? 'a tool'}` : PHASE_LABELS[stream.phase];
}

const EMPTY_STREAM: StreamState = { parts: [], usage: null, traceId: null, phase: null };

// The Markdown stack is ~58 kB gzipped and this admin page is the only thing
// that needs it, so it is kept out of the bundle every public visitor loads.
const LazyMarkdown = lazy(() =>
  import('../components/admin/agent/MarkdownMessage')
    .then(module => ({ default: module.MarkdownMessage })));

const PlainText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;

/** Falls back to the raw text while the Markdown chunk loads. */
function Markdown({ text }: { text: string }) {
  return (
    <Suspense fallback={<PlainText>{text}</PlainText>}>
      <LazyMarkdown text={text} />
    </Suspense>
  );
}

/** Appends streamed text to the open text part, starting one when needed. */
function appendText(parts: StreamPart[], text: string): StreamPart[] {
  const last = parts[parts.length - 1];
  if (last && last.kind === 'text' && !last.closed) {
    return [...parts.slice(0, -1), { ...last, text: last.text + text }];
  }
  return [...parts, { kind: 'text', text, closed: false }];
}

/** Replaces the open text part with the server's authoritative text and closes it. */
function closeText(parts: StreamPart[], text: string): StreamPart[] {
  const last = parts[parts.length - 1];
  if (last && last.kind === 'text' && !last.closed) {
    return [...parts.slice(0, -1), { ...last, text: text || last.text, closed: true }];
  }
  return text ? [...parts, { kind: 'text', text, closed: true }] : parts;
}

function updateTool(
  parts: StreamPart[],
  toolCallId: unknown,
  patch: Partial<ToolActivity>
): StreamPart[] {
  return parts.map(part =>
    part.kind === 'tool' && part.tool.toolCallId === toolCallId
      ? { ...part, tool: { ...part.tool, ...patch } }
      : part);
}

export function AdminAgentPage() {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [approvals, setApprovals] = useState<ApprovalSummary[]>([]);
  const [excluded, setExcluded] = useState<Record<string, string[]>>({});
  const [prompt, setPrompt] = useState('');
  const [stream, setStream] = useState<StreamState>(EMPTY_STREAM);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [models, setModels] = useState<AgentModel[]>([]);
  // Remembered per browser: a slower model is a deliberate choice, not a default
  // to be re-made on every visit.
  const [model, setModel] = useState<string>(() => localStorage.getItem(MODEL_KEY) ?? '');
  const defaultModelLabel = models.find(entry => entry.isDefault)?.label ?? '';

  const abortRef = useRef<(() => void) | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const approvalsInFlight = useRef(false);
  const approvalsQueued = useRef(false);
  // The server creates the conversation on the first turn, so its id arrives on
  // the stream. A ref avoids reading a stale activeId inside the send closure.
  const conversationIdRef = useRef<string | null>(null);

  const refreshConversations = useCallback(async () => {
    try {
      const { conversations: list } = await listConversations();
      setConversations(list);
    } catch (loadError) {
      setError((loadError as Error).message);
    }
  }, []);

  const refreshApprovals = useCallback(async () => {
    // A turn can raise many approval events in quick succession; without this
    // each one fired its own request and production saw 90 in a single minute.
    if (approvalsInFlight.current) {
      approvalsQueued.current = true;
      return;
    }
    approvalsInFlight.current = true;
    try {
      do {
        approvalsQueued.current = false;
        const { approvals: pending } = await listApprovals();
        setApprovals(pending);
      } while (approvalsQueued.current);
    } catch {
      // Approvals are supplementary; a failure here must not blank the chat.
    } finally {
      approvalsInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    refreshConversations();
    refreshApprovals();
    listModels()
      .then(result => setModels(result.models))
      .catch(() => setModels([]));
  }, [refreshConversations, refreshApprovals]);

  useEffect(() => {
    if (!activeId) {
      setMessages([]);
      return;
    }
    getTranscript(activeId)
      .then(result => setMessages(result.messages))
      .catch(loadError => setError((loadError as Error).message));
  }, [activeId]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight });
  }, [messages, stream.parts, stream.phase]);

  const handleEvent = useCallback((event: AgentEvent) => {
    switch (event.type) {
      case 'trace':
        setStream(current => ({ ...current, traceId: String(event.traceId ?? '') }));
        if (event.conversationId) {
          conversationIdRef.current = String(event.conversationId);
          if (!activeId) setActiveId(String(event.conversationId));
        }
        break;
      case 'reasoning': {
        // A reasoning model can spend a minute here; showing the summary is the
        // only evidence the turn is progressing.
        const text = String(event.text ?? '').trim();
        setStream(current => ({
          ...current,
          phase: 'thinking',
          parts: text ? [...current.parts, { kind: 'reasoning', text }] : current.parts,
        }));
        break;
      }
      case 'message.started':
        setStream(current => ({ ...current, phase: 'writing' }));
        break;
      case 'message.delta':
        setStream(current => ({
          ...current,
          phase: 'writing',
          parts: appendText(current.parts, String(event.text ?? '')),
        }));
        break;
      case 'message.completed':
        setStream(current => ({
          ...current,
          parts: closeText(current.parts, String(event.text ?? '')),
        }));
        break;
      case 'tool.started':
        setStream(current => ({
          ...current,
          phase: 'working',
          parts: [...current.parts, {
            kind: 'tool',
            tool: {
              toolCallId: (event.toolCallId as string) ?? null,
              toolName: (event.toolName as string) ?? null,
              status: 'running',
              inputSummary: (event.inputSummary as string) ?? null,
            },
          }],
        }));
        break;
      case 'tool.completed':
      case 'tool.failed':
        setStream(current => ({
          ...current,
          phase: 'working',
          parts: updateTool(current.parts, event.toolCallId, {
            status: event.type === 'tool.failed' ? 'failed' : 'completed',
            resultSummary: (event.resultSummary as string) ?? null,
            durationMs: (event.durationMs as number) ?? null,
          }),
        }));
        break;
      case 'approval.required':
        setStream(current => ({ ...current, phase: 'approval' }));
        refreshApprovals();
        break;
      case 'approval.resolved':
        setStream(current => ({ ...current, phase: 'working' }));
        break;
      case 'usage':
        setStream(current => ({ ...current, usage: event as Record<string, unknown> }));
        break;
      case 'error':
        setError(`${event.error}: ${event.message}${event.traceId ? ` (trace ${event.traceId})` : ''}`);
        break;
      default:
        break;
    }
  }, [activeId, refreshApprovals]);

  const submit = useCallback(async (submitted: string) => {
    if (!submitted.trim() || sending) return;
    setError(null);
    setSending(true);
    // Feedback has to appear before the first server event, which can be
    // several seconds away while the model reasons.
    setStream({ ...EMPTY_STREAM, phase: 'working' });
    conversationIdRef.current = activeId;

    const optimistic: TranscriptMessage = {
      messageId: `local-${Date.now()}`,
      sequence: messages.length,
      role: 'user',
      content: submitted,
      createdAt: new Date().toISOString(),
    };
    setMessages(current => [...current, optimistic]);

    const { done, abort } = sendMessage(
      { conversationId: activeId ?? undefined, prompt: submitted, model: model || undefined },
      handleEvent
    );
    abortRef.current = abort;

    try {
      await done;
    } catch (sendError) {
      setError((sendError as Error).message);
    } finally {
      abortRef.current = null;
      setSending(false);

      // The final message is persisted server-side, so the reloaded transcript
      // is authoritative. The streamed text is only cleared once that reload
      // succeeds; otherwise the answer would disappear from the screen.
      const conversationId = conversationIdRef.current;
      let reloaded = false;
      if (conversationId) {
        try {
          const result = await getTranscript(conversationId);
          setMessages(result.messages);
          reloaded = true;
        } catch {
          reloaded = false;
        }
      }
      if (reloaded) setStream(EMPTY_STREAM);

      await Promise.all([refreshConversations(), refreshApprovals()]);
    }
  }, [activeId, handleEvent, messages.length, model, refreshApprovals, refreshConversations, sending]);

  const onSubmit = (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    const submitted = prompt;
    setPrompt('');
    submit(submitted);
  };

  const onApprovalResponse = async (
    approval: ApprovalSummary,
    decision: 'approved' | 'rejected'
  ) => {
    try {
      await respondToApproval(approval.approvalId, decision, {
        excludedRowIds: excluded[approval.approvalId] ?? [],
      });
      await refreshApprovals();
    } catch (responseError) {
      setError((responseError as Error).message);
    }
  };

  const runSearch = async () => {
    if (!query.trim()) {
      refreshConversations();
      return;
    }
    try {
      const { results } = await searchConversations(query);
      setConversations(results as ConversationSummary[]);
    } catch (searchError) {
      setError((searchError as Error).message);
    }
  };

  const visibleApprovals = useMemo(
    () => approvals.filter(approval => !activeId || approval.conversationId === activeId),
    [approvals, activeId]
  );

  return (
    <AdminLayout
      currentPage="/admin/agent"
      pageTitle="Agent"
      pageDescription="Conversational admin assistant for Event Finder"
    >
      <Shell>
        <Sidebar aria-label="Conversations">
          <IconButton type="button" onClick={() => { setActiveId(null); setMessages([]); }}>
            <MessageSquarePlus size={16} aria-hidden /> New chat
          </IconButton>

          <SearchRow>
            <SearchInput
              value={query}
              onChange={changeEvent => setQuery(changeEvent.target.value)}
              onKeyDown={keyEvent => keyEvent.key === 'Enter' && runSearch()}
              placeholder="Search conversations"
              aria-label="Search conversations"
            />
            <IconButton type="button" onClick={runSearch} aria-label="Run search">
              <Search size={16} aria-hidden />
            </IconButton>
          </SearchRow>

          {conversations.length === 0 && <Notice>No conversations yet.</Notice>}
          {conversations.map(conversation => (
            <ConversationButton
              key={conversation.conversationId}
              $active={conversation.conversationId === activeId}
              onClick={() => setActiveId(conversation.conversationId)}
              aria-current={conversation.conversationId === activeId ? 'true' : undefined}
            >
              <div>{conversation.title}</div>
              <small style={{ color: colors.text.tertiary }}>
                {new Date(conversation.updatedAt).toLocaleDateString()} ·{' '}
                {conversation.messageCount} msg
              </small>
            </ConversationButton>
          ))}
        </Sidebar>

        <Main>
          <Transcript ref={transcriptRef} aria-live="polite" aria-label="Conversation transcript">
            {messages.length === 0 && stream.parts.length === 0 && !sending && (
              <Notice>
                Ask about events, entities or event sources. For example: &ldquo;Show Elastic
                events in Las Vegas in October&rdquo;.
              </Notice>
            )}

            {messages.map(message => (
              <div key={message.messageId}>
                {/* Tools run before the reply they inform, so they render above it. */}
                {(message.activity ?? []).map((entry, index) => (
                  <ToolCard
                    key={`${message.messageId}-${index}`}
                    activity={{
                      toolCallId: `${message.messageId}-${index}`,
                      toolName: entry.toolName ?? entry.summary ?? 'tool',
                      status: 'completed',
                      resultSummary: entry.summary ?? null,
                    }}
                  />
                ))}
                {message.content.trim() && (
                  <Bubble $role={message.role}>
                    {message.role === 'assistant'
                      ? <Markdown text={message.content} />
                      : message.content}
                  </Bubble>
                )}
                {message.usage && (
                  <UsageCard usage={{ ...message.usage, model: message.model, reasoningEffort: message.reasoningEffort }} />
                )}
              </div>
            ))}

            {stream.parts.map((part, index) => (
              part.kind === 'tool'
                ? (
                  <ToolCard
                    key={part.tool.toolCallId ?? `tool-${index}`}
                    activity={part.tool}
                    traceId={stream.traceId}
                  />
                )
                : part.kind === 'reasoning' ? (
                  <ReasoningNote key={`reasoning-${index}`}>{part.text}</ReasoningNote>
                ) : part.text.trim() ? (
                  <Bubble key={`text-${index}`} $role="assistant">
                    <Markdown text={part.text} />
                  </Bubble>
                ) : null
            ))}

            {sending && statusLabel(stream) && (
              <ActivityIndicator label={statusLabel(stream) as string} />
            )}

            {stream.usage && <UsageCard usage={{ ...stream.usage, traceId: stream.traceId }} />}

            {visibleApprovals.map(approval => (
              <ApprovalCard
                key={approval.approvalId}
                approval={approval}
                excludedRowIds={excluded[approval.approvalId] ?? []}
                onToggleRow={rowId => setExcluded(current => {
                  const list = current[approval.approvalId] ?? [];
                  return {
                    ...current,
                    [approval.approvalId]: list.includes(rowId)
                      ? list.filter(item => item !== rowId)
                      : [...list, rowId],
                  };
                })}
                onRespond={decision => onApprovalResponse(approval, decision)}
              />
            ))}

            {error && <Notice role="alert" style={{ color: colors.error[600] }}>{error}</Notice>}

            {showUpload && <UploadPanel conversationId={activeId ?? undefined} />}
          </Transcript>

          <Composer onSubmit={onSubmit}>
            <IconButton
              type="button"
              onClick={() => setShowUpload(current => !current)}
              aria-label={showUpload ? 'Hide upload panel' : 'Show upload panel'}
              aria-pressed={showUpload}
            >
              <Paperclip size={16} aria-hidden />
            </IconButton>
            <TextArea
              value={prompt}
              onChange={changeEvent => setPrompt(changeEvent.target.value)}
              onKeyDown={keyEvent => {
                if (keyEvent.key === 'Enter' && !keyEvent.shiftKey) {
                  keyEvent.preventDefault();
                  onSubmit(keyEvent);
                }
              }}
              placeholder="Ask the Event Finder agent"
              aria-label="Message"
              disabled={sending}
            />
            {models.length > 1 && (
              <ModelSelect
                value={model}
                onChange={changeEvent => {
                  setModel(changeEvent.target.value);
                  localStorage.setItem(MODEL_KEY, changeEvent.target.value);
                }}
                aria-label="Model"
                disabled={sending}
              >
                <option value="">
                  Default{defaultModelLabel ? ` (${defaultModelLabel})` : ''}
                </option>
                {models.map(entry => (
                  <option key={entry.model} value={entry.model}>
                    {entry.label}
                  </option>
                ))}
              </ModelSelect>
            )}
            {sending ? (
              <IconButton type="button" onClick={() => abortRef.current?.()} aria-label="Stop">
                <Square size={16} aria-hidden /> Stop
              </IconButton>
            ) : (
              <IconButton type="submit" disabled={!prompt.trim()} aria-label="Send">
                <Send size={16} aria-hidden /> Send
              </IconButton>
            )}
          </Composer>
        </Main>
      </Shell>

      {activeId && (
        <IconButton
          type="button"
          style={{ marginTop: spacing[3] }}
          onClick={async () => {
            await archiveConversation(activeId);
            setActiveId(null);
            refreshConversations();
          }}
        >
          <Archive size={16} aria-hidden /> Archive conversation
        </IconButton>
      )}
    </AdminLayout>
  );
}

export default AdminAgentPage;
